// Web NFC (NDEF) helpers. Progressive enhancement: everything degrades gracefully when
// NDEFReader is unavailable (desktop, iOS, unsupported browsers). The physical radio write/
// read can only run on Android Chrome over HTTPS with a real tag.

export function nfcWriteSupported() {
  return typeof window !== "undefined" && "NDEFReader" in window;
}

// Map raw Web NFC errors to friendly, actionable states.
export function nfcErrorInfo(err) {
  const name = err?.name || "";
  if (name === "AbortError") return { kind: "cancelled", message: "NFC programming cancelled." };
  if (name === "NotAllowedError") return { kind: "disabled", message: "NFC permission was denied, or NFC appears to be switched off. Please enable NFC and try again." };
  if (name === "NotSupportedError") return { kind: "unsupported", message: "NFC writing is not supported by this browser/device." };
  if (name === "NotReadableError" || name === "NetworkError") return { kind: "failed", message: "Unable to communicate with the NFC tag. It may be locked, read-only or moved too soon." };
  return { kind: "failed", message: err?.message || "Unable to programme NFC tag." };
}

// --- Shared write session -------------------------------------------------------------------
// Android pops up its own system "New tag" dialog (and the web app never writes) whenever NO Web
// NFC operation is active at the instant the tag is tapped. Keeping a persistent scan() running on
// the SAME NDEFReader holds the radio in reader mode for the whole write flow, which suppresses the
// OS Tag dispatcher so our write() reliably captures the tag. End the session before doing a
// standalone read (verify) so two readers don't fight over the radio.
let _session = null; // { reader, ac }

async function _ensureSession() {
  if (!nfcWriteSupported()) { const e = new Error("unsupported"); e.name = "NotSupportedError"; throw e; }
  if (!_session) {
    const reader = new window.NDEFReader();
    const ac = new AbortController();
    await reader.scan({ signal: ac.signal }); // holds NFC in reader mode → suppresses OS "New tag"
    _session = { reader, ac };
  }
  return _session.reader;
}

// Release the held NFC reader. Safe to call any time (e.g. dialog close, before a verify read).
export function nfcEndSession() {
  if (_session?.ac) { try { _session.ac.abort(); } catch { /* noop */ } }
  _session = null;
}

// Write a URL NDEF record. overwrite=false first so we never silently clobber existing data; the
// caller offers explicit overwrite on rejection. The active scan session keeps the radio engaged.
export async function nfcWrite(url, { overwrite = false } = {}) {
  const reader = await _ensureSession();
  await reader.write({ records: [{ recordType: "url", data: url }] }, { overwrite });
}

// Read a single NDEF message and return { url, serialNumber }. Resolves on first read; the
// caller should show a "tap the tag" prompt. Times out so the UI never hangs forever.
export function nfcReadOnce({ timeoutMs = 25000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!nfcWriteSupported()) { const e = new Error("unsupported"); e.name = "NotSupportedError"; return reject(e); }
    nfcEndSession(); // never run a standalone read while a write session holds the radio
    const reader = new window.NDEFReader();
    const ac = new AbortController();
    const timer = setTimeout(() => { ac.abort(); const e = new Error("timeout"); e.name = "AbortError"; reject(e); }, timeoutMs);
    reader.onreading = (event) => {
      clearTimeout(timer);
      let url = null;
      try {
        for (const rec of event.message.records) {
          if (rec.recordType === "url" || rec.recordType === "text") {
            url = new TextDecoder(rec.encoding || "utf-8").decode(rec.data);
            if (rec.recordType === "url") break;
          }
        }
      } catch { /* ignore decode errors */ }
      ac.abort();
      resolve({ url, serialNumber: event.serialNumber || null });
    };
    reader.onreadingerror = () => { clearTimeout(timer); ac.abort(); const e = new Error("read failed"); e.name = "NotReadableError"; reject(e); };
    reader.scan({ signal: ac.signal }).catch((e) => { clearTimeout(timer); reject(e); });
  });
}

// Extract the opaque token from a full /t/{token} URL or a bare token string.
export function tokenFromUrl(v) {
  if (!v) return null;
  v = String(v).trim();
  if (v.includes("/t/")) return v.split("/t/")[1].split(/[?#]/)[0].replace(/\/+$/, "");
  return v;
}
